import { FilterIcon, Inbox } from "lucide-react";
import { useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../ui/button";
import { ActiveFiltersSummaryList } from "./ActiveFiltersSummary/ActiveFiltersSummaryList";
import {
  buildActiveFilterDescriptors,
  resolveActiveFilterColumnVisibilityToggle,
} from "../utils/activeFilterDescriptors";
import { useDataTableOptions } from "../hooks/useDataTableOptions";
import { useDataTableSettings } from "../hooks/useDataTableSettings";
import useTableColumnVisibility from "../hooks/useTableColumnVisibility";
import { cn } from "@/lib/utils";

export default function EmptyState() {
  const { t } = useTranslation();
  const options = useDataTableOptions();
  const { table } = useDataTableSettings();
  const { columnVisibility } = useTableColumnVisibility();

  const filterDescriptors = useMemo(
    () =>
      buildActiveFilterDescriptors(table, options.rawFilters, columnVisibility),
    [table, options.rawFilters, columnVisibility],
  );

  const getColumnVisibilityToggle = useCallback(
    (columnId: string) =>
      resolveActiveFilterColumnVisibilityToggle(table, columnId),
    [table],
  );

  const isFiltered = options.isFiltered();
  const hasFiltersList = filterDescriptors.length > 0;
  /** No query modifiers: show a larger illustration centered in the viewport. */
  const showCenteredHero =
    !isFiltered && !options.isSorted() && !hasFiltersList;

  const primaryMessage = isFiltered
    ? t("table.empty.noRowsMatchFilters")
    : t("table.noData");

  /** Standalone layout (no filter chips above the table): compact or hero-sized icon + title. */
  const headlineStandalone = (
    <div
      className={cn(
        "flex flex-col items-center text-center",
        showCenteredHero ? "gap-5 md:gap-6" : "gap-3",
      )}
    >
      <div
        className={cn(
          "flex items-center justify-center rounded-full bg-white/80 text-slate-600 shadow-sm ring-1 ring-slate-300/60 dark:bg-slate-800/80 dark:text-slate-300 dark:ring-slate-600/60",
          showCenteredHero
            ? "h-16 w-16 md:h-[4.25rem] md:w-[4.25rem]"
            : "h-11 w-11",
        )}
      >
        <Inbox
          className={cn(
            showCenteredHero
              ? "h-8 w-8 md:h-9 md:w-9"
              : "h-[1.35rem] w-[1.35rem]",
          )}
          strokeWidth={1.75}
        />
      </div>
      <h2
        className={cn(
          "text-balance font-semibold leading-snug tracking-tight text-slate-900 dark:text-slate-50",
          showCenteredHero
            ? "text-lg md:text-xl"
            : "text-base md:text-[1.05rem]",
        )}
      >
        {primaryMessage}
      </h2>
    </div>
  );

  const titleOnly = (
    <h2 className="text-balance text-base font-semibold leading-snug tracking-tight text-slate-900 dark:text-slate-50 md:text-[1.05rem]">
      {primaryMessage}
    </h2>
  );

  return (
    <div
      data-testid="empty-state"
      className={cn(
        "flex w-full flex-1 flex-col items-center overflow-y-auto whitespace-normal",
        "bg-gradient-to-b from-slate-200/95 via-slate-200 to-slate-300/80",
        showCenteredHero
          ? "min-h-[min(420px,55vh)] justify-center px-4 py-10 md:min-h-[min(520px,65vh)] md:px-8 md:py-14"
          : "min-h-[min(360px,50vh)] px-4 py-8 md:min-h-[min(480px,60vh)] md:px-8",
      )}
    >
      <div className="flex w-full max-w-5xl flex-col items-center gap-6 text-center">
        {hasFiltersList ? (
          <div className="relative mt-14 w-full overflow-visible pb-px">
            <div
              className={cn(
                "relative w-full overflow-visible rounded-2xl border border-slate-300/90 px-4 pb-4 pt-14",
                "bg-slate-50/95 shadow-[0_2px_8px_rgba(15,23,42,0.06)] ring-1 ring-slate-950/[0.04]",
                "dark:border-slate-600 dark:bg-slate-900/55 dark:shadow-black/20 dark:ring-white/[0.06]",
                "sm:px-5 sm:pb-5 sm:pt-16",
              )}
            >
              {/* Center sits on card top edge: half in hero, half in background */}
              <div
                className={cn(
                  "absolute left-1/2 top-0 z-[1] flex size-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full",
                  "bg-white/95 text-slate-600 shadow-[0_8px_20px_-4px_rgba(15,23,42,0.18)]",
                  "ring-2 ring-white ring-offset-2 ring-offset-slate-50/95",
                  "dark:bg-slate-800/95 dark:text-slate-300 dark:ring-offset-slate-900/95",
                  "sm:size-16",
                )}
              >
                <Inbox
                  className="size-8 sm:size-[2.125rem]"
                  strokeWidth={1.65}
                />
              </div>

              <div className="flex flex-col gap-5 sm:gap-6">
                <div className="text-center">{titleOnly}</div>
                <ActiveFiltersSummaryList
                  descriptors={filterDescriptors}
                  layout="surface"
                  onRemoveColumnFilter={options.resetFilterById}
                  getColumnVisibilityToggle={getColumnVisibilityToggle}
                />
              </div>
            </div>
          </div>
        ) : (
          headlineStandalone
        )}

        {options.isFiltered() && (
          <div className="flex w-full max-w-5xl flex-col items-center pt-1">
            <div
              className={cn(
                "flex w-full flex-wrap items-center justify-center gap-2",
                hasFiltersList &&
                  "border-t border-slate-300/60 pt-5 dark:border-slate-600/50",
              )}
            >
              <Button
                variant="default"
                size="sm"
                onClick={options.resetFilters}
                className="grow sm:grow-0 sm:min-w-[9.5rem]"
              >
                <FilterIcon className="h-4 w-4" /> {t("table.clearFilters")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
